import { useEffect, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  fetchVariablesStart,
  fetchVariablesSuccess,
  fetchVariablesFailure,
  clearVariables,
} from "@/store/slices/silaeVariablesSlice";
import apiClient from "@/services/apiClient";
import type { SilaeVariable } from "@/types/silae";

/**
 * Hook personnalisé pour charger et utiliser les variables Silae depuis Redux
 * Les variables sont chargées une seule fois au démarrage et après chaque reload
 * (pas de persistence localStorage pour avoir toujours des données à jour)
 * Ne charge les variables que si l'utilisateur est authentifié
 */
export function useSilaeVariables() {
  const dispatch = useAppDispatch();
  const { variables, isLoading, isLoaded, error } = useAppSelector(
    (state) => state.silaeVariables,
  );
  const { user } = useAppSelector((state) => state.auth);

  /**
   * Charge les variables depuis l'API
   */
  const loadVariables = useCallback(async () => {
    // Ne pas charger si l'utilisateur n'est pas authentifié
    if (!user) {
      return;
    }

    try {
      dispatch(fetchVariablesStart());

      // Récupérer toutes les variables actives depuis l'API
      const response = await apiClient.get<{
        data: SilaeVariable[];
        total: number;
      }>("/api/silae-variables", {
        params: {
          isActive: true,
          limit: 10000, // Charger toutes les variables
        },
      });

      dispatch(fetchVariablesSuccess(response.data.data));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Erreur lors du chargement des variables Silae";
      dispatch(fetchVariablesFailure(errorMessage));
      console.error("Error loading Silae variables:", err);
    }
  }, [dispatch, user]);

  /**
   * Charge automatiquement les variables au montage si elles ne sont pas déjà chargées
   * et si l'utilisateur est authentifié
   * Recharge aussi les variables quand l'utilisateur se connecte
   */
  useEffect(() => {
    if (user && !isLoading) {
      // Charger si pas encore chargé, ou recharger si l'utilisateur vient de se connecter
      if (!isLoaded) {
        loadVariables();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoaded, isLoading]); // On exclut loadVariables pour éviter la boucle infinie

  /**
   * Réinitialiser les variables quand l'utilisateur se déconnecte
   */
  useEffect(() => {
    if (!user && isLoaded) {
      dispatch(clearVariables());
    }
  }, [user, isLoaded, dispatch]);

  /**
   * Fonction pour rechercher des variables
   */
  const searchVariables = useCallback(
    (searchTerm: string): SilaeVariable[] => {
      if (!searchTerm.trim()) {
        return variables;
      }

      const term = searchTerm.toLowerCase();
      return variables.filter(
        (variable) =>
          variable.code.toLowerCase().includes(term) ||
          variable.definition.toLowerCase().includes(term) ||
          (variable.label && variable.label.toLowerCase().includes(term)),
      );
    },
    [variables],
  );

  /**
   * Fonction pour trouver une variable par son code
   */
  const findVariable = useCallback(
    (code: string): SilaeVariable | undefined => {
      return variables.find((variable) => variable.code === code);
    },
    [variables],
  );

  /**
   * Obtenir les variables fréquemment utilisées
   */
  const frequentlyUsedVariables = useMemo(() => {
    return variables.filter((variable) => variable.isFrequentlyUsed);
  }, [variables]);

  /**
   * Obtenir les variables par catégorie
   */
  const getVariablesByCategory = useCallback(
    (category: string): SilaeVariable[] => {
      return variables.filter((variable) => variable.category === category);
    },
    [variables],
  );

  /**
   * Obtenir les variables par type
   */
  const getVariablesByType = useCallback(
    (type: string): SilaeVariable[] => {
      return variables.filter((variable) => variable.type === type);
    },
    [variables],
  );

  /**
   * Filtrer les variables (exclure les fonctions par défaut)
   */
  const filterVariables = useCallback(
    (excludeTypes: string[] = ["function"]): SilaeVariable[] => {
      return variables.filter(
        (variable) => !excludeTypes.includes(variable.type),
      );
    },
    [variables],
  );

  /**
   * Fonction pour retenter le chargement des variables
   * Utile si le premier chargement a échoué (par exemple, API non disponible)
   */
  const retryLoadVariables = useCallback(() => {
    // Réinitialiser l'état avant de retenter
    dispatch(clearVariables());
    loadVariables();
  }, [dispatch, loadVariables]);

  return {
    // État
    variables,
    isLoading,
    isLoaded,
    error,

    // Fonctions
    loadVariables,
    retryLoadVariables,
    searchVariables,
    findVariable,
    frequentlyUsedVariables,
    getVariablesByCategory,
    getVariablesByType,
    filterVariables,
  };
}
